/**
 * HeroCanvas — the landing hero's full-bleed atmospheric layer (D-C1).
 *
 * Extracted from Hero.tsx so Hero stays about CONTENT and the canvas owns its
 * own composition + test. Pure presentational: no hooks, no props, no state.
 * Decoration only — `aria-hidden` + `pointer-events-none` on the root, so it is
 * invisible to assistive tech and never intercepts a click.
 *
 * WHY IT IS TIERED (D-C2). The five original layers are fixed-px and anchored to
 * viewport fractions, so on a 27" monitor they cluster in the middle ~1200px and
 * the outer thirds are flat navy — the page reads letterboxed rather than
 * composed. The fix is additive and only at the widths where the problem exists:
 *   < 2xl  (<1536) — today's five layers, visually unchanged. Phones, tablets
 *                    and ordinary laptops were never the problem and pay
 *                    nothing at all.
 *   >= 2xl (1536+) — the side orbs scale up and migrate into the outer thirds,
 *                    and two low-alpha orbs plus a horizon band join them.
 *
 * ENTIRELY STATIC. D-C3 originally gave the orbs an ambient drift; it was
 * measured out on 2026-07-31 (Godfred's call) after it proved to cost ~2 of the
 * 3 Lighthouse points the canvas was spending at >=1536, for motion its own WCAG
 * defence described as sub-perceptual. The composition — which is where the
 * measured value is, outer-third tonal variation roughly doubling at 1920 and
 * 2560 — is untouched. Numbers at the NO ANIMATION note in globals.css.
 *
 * Geometry, drift keyframes and the D-C6 central-band arithmetic live in
 * globals.css under `--canvas-safe-edge`: every 2xl offset derives from ONE
 * constant (the protected 960px band the hero text composites over), and the
 * drift has to be viewport-gated, which a utility class cannot express.
 * Colors are existing utilities only (D-C4) — no new tokens, no tailwind-config
 * change, so the ESM/CJS inline-mirror trap is dodged entirely.
 *
 * NOT storm-coupled (D-C5): the storm band already carries that information, and
 * making decoration a second information channel is a dim-14 defect plus a
 * maintenance contract with zero task value. One treatment, all states.
 */
export function HeroCanvas() {
  return (
    <div className="hero-canvas absolute inset-0 pointer-events-none" aria-hidden>
      {/* L1 — secondary, upper. Base: fixed 600px at left-1/4. 2xl: 34vw, migrated
          into the left outer third (see .canvas-l1). */}
      <div className="canvas-l1 absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-brand-secondary opacity-30 blur-3xl" />

      {/* L2 — primary, lower. Base: fixed 500px at right-1/4. 2xl: 30vw, migrated
          into the right outer third. */}
      <div className="canvas-l2 absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-brand-primary opacity-20 blur-3xl" />

      {/* L3 — accent, centered. Stays behind the content column at every tier, so
          the hero text composites over exactly what it does today (D-C6).
          Centered by calc rather than -translate-x/y-1/2: pixel-identical, and
          it keeps `transform` free should the drift ever be revisited. */}
      <div className="canvas-l3 absolute top-[calc(50%_-_200px)] left-[calc(50%_-_200px)] w-[400px] h-[400px] rounded-full bg-brand-accent opacity-10 blur-3xl" />

      {/* L4 / L5 — white corner accents. Unchanged at every tier and undrifted:
          they are corner punctuation, not field composition. */}
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white opacity-[0.04]" />
      <div className="absolute -bottom-12 -left-12 w-64 h-64 rounded-full bg-white opacity-[0.04]" />

      {/* L6 / L7 — NEW, 2xl only. Low-alpha orbs at mid-height that give the outer
          thirds a second depth plane behind the migrated L1/L2. Each side carries
          both brand hues at different heights, which is what stops the wide field
          reading as two lonely blobs. */}
      <div className="canvas-l6 hidden 2xl:block absolute rounded-full bg-brand-primary opacity-[0.12] blur-3xl" />
      <div className="canvas-l7 hidden 2xl:block absolute rounded-full bg-brand-secondary opacity-[0.12] blur-3xl" />

      {/* L8 — NEW, 2xl only. A sea-horizon suggestion: a full-width band of light
          along the bottom edge. Exempt from the lateral central-band rule because
          it is full-width by design, but it sits entirely BELOW the content
          column's last text block — it lives inside the section's 96px bottom
          padding, which is locale-invariant, so es wrapping taller moves the text
          and the section together and never into the band. Unblurred and
          undrifted: a horizon that wobbles is not a horizon. */}
      <div className="canvas-horizon hidden 2xl:block absolute" />
    </div>
  );
}
